const ATTEMPT_KEY = 'scrambl-auto-refresh-attempts-v1'
let tracking = false
let mayHaveEdits = false
let refreshStartedAt: number | null = null
let hasPendingWork: () => boolean = () => true

/** Stay conservative after editing, even if a route unmounts a draft's controls. */
export function trackRefreshSafety(pendingWork: () => boolean): () => void {
  tracking = true
  hasPendingWork = pendingWork
  const markEdited = () => {
    mayHaveEdits = true
  }
  const markControlInteraction = (event: Event) => {
    if (
      event.target instanceof Element &&
      event.target.closest(
        'input, textarea, select, [contenteditable], [role="textbox"], [role="combobox"], [role="checkbox"], [role="switch"], [role="slider"]',
      )
    )
      markEdited()
  }
  const markPossibleAction = (event: Event) => {
    if (
      event instanceof KeyboardEvent &&
      ['Tab', 'Shift', 'Control', 'Alt', 'Meta'].includes(event.key)
    )
      return
    // Custom editors, drag handles and action buttons need not emit input/change.
    // Only navigation links and explicitly safe sign-in choices are exempt.
    if (
      !(event.target instanceof Element) ||
      !event.target.closest('a[href], [data-auto-refresh-safe]')
    )
      markEdited()
  }
  document.addEventListener('input', markEdited, true)
  document.addEventListener('change', markEdited, true)
  document.addEventListener('drop', markEdited, true)
  document.addEventListener('pointerdown', markPossibleAction, true)
  document.addEventListener('click', markPossibleAction, true)
  document.addEventListener('keydown', markPossibleAction, true)
  document.addEventListener('focusin', markControlInteraction, true)
  return () => {
    tracking = false
    document.removeEventListener('input', markEdited, true)
    document.removeEventListener('change', markEdited, true)
    document.removeEventListener('drop', markEdited, true)
    document.removeEventListener('pointerdown', markPossibleAction, true)
    document.removeEventListener('click', markPossibleAction, true)
    document.removeEventListener('keydown', markPossibleAction, true)
    document.removeEventListener('focusin', markControlInteraction, true)
  }
}

export function isPageRefreshing(): boolean {
  // A browser unload guard can cancel reload. Allow the manual prompt to return
  // in that case; the persisted attempt still prevents another automatic reload.
  return refreshStartedAt !== null && Date.now() - refreshStartedAt < 3000
}

export function tryAutomaticPageRefresh(
  currentVersion: string | undefined,
): boolean {
  if (isPageRefreshing()) return true
  if (!tracking || !currentVersion || mayHaveEdits || hasPendingWork())
    return false
  if (document.visibilityState !== 'visible' || !navigator.onLine) return false
  // Never interrupt a callback, verification step, or payment handoff.
  if (
    /\/(auth|callback|confirmation|verify|checkout|pay)(\/|$)/.test(
      window.location.pathname,
    )
  )
    return false
  if (
    document.querySelector(
      'form, [role="dialog"], [role="alertdialog"], [contenteditable]:not([contenteditable="false"]), [role="textbox"], [aria-busy="true"], textarea, select, input[type="file"]',
    )
  )
    return false
  // Includes autofilled and programmatically populated fields, not just input events.
  if (
    [...document.querySelectorAll('input')].some(
      input =>
        !['hidden', 'button', 'submit', 'reset'].includes(input.type) &&
        (['checkbox', 'radio'].includes(input.type)
          ? input.checked !== input.defaultChecked
          : !!input.value),
    )
  )
    return false

  try {
    const raw = sessionStorage.getItem(ATTEMPT_KEY)
    const attempts: unknown = raw ? JSON.parse(raw) : []
    if (
      !Array.isArray(attempts) ||
      attempts.some(value => typeof value !== 'string') ||
      attempts.includes(currentVersion)
    )
      return false
    // Record the running bundle, not the server version: even a stale or changing
    // manifest cannot keep reloading the same bundle. Never clear this on polls.
    sessionStorage.setItem(
      ATTEMPT_KEY,
      JSON.stringify([...attempts, currentVersion]),
    )
  } catch {
    // Without a durable receipt we cannot guarantee protection from a reload loop.
    return false
  }
  refreshStartedAt = Date.now()
  window.location.reload()
  return true
}
